import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './admin.css';
import NavbarSettings from './NavbarSettings';
import UpperNavSettings from './UpperNavSettings';
import HeroSettings from './HeroSettings';
import ServicesSettings from './ServicesSettings';
import AboutSettings from './AboutSettings';
import WhatYouGetSettings from './WhatYouGetSettings';
import FaqSettings from './FaqSettings';
import ContactSettings from './ContactSettings';
import FooterSettings from './FooterSettings';
import BlogsSettings from './BlogsSettings';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    const fetchData = async () => {
      try {
        const [meRes, summaryRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/admin/me`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE_URL}/api/admin/summary`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setAdmin(meRes.data.admin);
        setStats(summaryRes.data.stats);
      } catch (err) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminInfo');
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminInfo');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="admin-dashboard-container">
        <div className="admin-dashboard-main">
          <div className="admin-dashboard-header">
            <h1>لوحة تحكم نبض كير</h1>
          </div>
          <p>جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      <aside className="admin-dashboard-sidebar">
        <div className="admin-sidebar-header">
          <h2>نبض كير</h2>
          <p>لوحة الإدارة</p>
        </div>
        <nav className="admin-sidebar-nav">
          <button
            type="button"
            className={`admin-sidebar-link ${activeSection === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveSection('overview')}
          >
            اللوحة الرئيسية
          </button>
          <button
            type="button"
            className={`admin-sidebar-link ${activeSection === 'navbar' ? 'active' : ''}`}
            onClick={() => setActiveSection('navbar')}
          >
            إعدادات قائمة الموقع
          </button>
          <button
            type="button"
            className={`admin-sidebar-link ${activeSection === 'upperNav' ? 'active' : ''}`}
            onClick={() => setActiveSection('upperNav')}
          >
            إعدادات الشريط العلوي
          </button>
          <button
            type="button"
            className={`admin-sidebar-link ${activeSection === 'hero' ? 'active' : ''}`}
            onClick={() => setActiveSection('hero')}
          >
            إعدادات قسم الهيرو
          </button>
          <button
            type="button"
            className={`admin-sidebar-link ${activeSection === 'services' ? 'active' : ''}`}
            onClick={() => setActiveSection('services')}
          >
            إدارة الخدمات
          </button>
          <button
            type="button"
            className={`admin-sidebar-link ${activeSection === 'about' ? 'active' : ''}`}
            onClick={() => setActiveSection('about')}
          >
            إعدادات من نحن
          </button>
          <button
            type="button"
            className={`admin-sidebar-link ${activeSection === 'whatYouGet' ? 'active' : ''}`}
            onClick={() => setActiveSection('whatYouGet')}
          >
            إعدادات ماذا ستحصل معنا
          </button>
          <button
            type="button"
            className={`admin-sidebar-link ${activeSection === 'faqs' ? 'active' : ''}`}
            onClick={() => setActiveSection('faqs')}
          >
            إعدادات الأسئلة الشائعة
          </button>
          <button
            type="button"
            className={`admin-sidebar-link ${activeSection === 'contact' ? 'active' : ''}`}
            onClick={() => setActiveSection('contact')}
          >
            إعدادات تواصل معنا
          </button>
          <button
            type="button"
            className={`admin-sidebar-link ${activeSection === 'footer' ? 'active' : ''}`}
            onClick={() => setActiveSection('footer')}
          >
            إعدادات الفوتر
          </button>
          <button
            type="button"
            className={`admin-sidebar-link ${activeSection === 'blogs' ? 'active' : ''}`}
            onClick={() => setActiveSection('blogs')}
          >
            إدارة المدونة
          </button>
        </nav>
        <button className="admin-sidebar-logout" onClick={handleLogout}>
          تسجيل الخروج
        </button>
      </aside>

      <main className="admin-dashboard-main">
        <header className="admin-dashboard-header">
          <div>
            <h1>مرحباً، {admin?.name || 'المسؤول'}</h1>
            <p>هذه هي لوحة تحكم نبض كير.</p>
          </div>
          <div className="admin-header-info">
            <span>{admin?.email}</span>
          </div>
        </header>

        {activeSection === 'overview' && (
          <section className="admin-dashboard-grid">
            <div className="admin-card">
              <h3>المرضى</h3>
              <p className="admin-card-number">{stats?.totalPatients ?? 0}</p>
              <p className="admin-card-caption">إجمالي عدد المرضى المسجلين</p>
            </div>
            <div className="admin-card">
              <h3>المواعيد</h3>
              <p className="admin-card-number">{stats?.totalAppointments ?? 0}</p>
              <p className="admin-card-caption">إجمالي عدد المواعيد</p>
            </div>
            <div className="admin-card">
              <h3>الرسائل</h3>
              <p className="admin-card-number">{stats?.totalMessages ?? 0}</p>
              <p className="admin-card-caption">الرسائل الواردة من الموقع</p>
            </div>
          </section>
        )}

        {activeSection === 'navbar' && <NavbarSettings />}
        {activeSection === 'upperNav' && <UpperNavSettings />}
        {activeSection === 'hero' && <HeroSettings />}
        {activeSection === 'services' && <ServicesSettings />}
        {activeSection === 'about' && <AboutSettings />}
        {activeSection === 'whatYouGet' && <WhatYouGetSettings />}
        {activeSection === 'faqs' && <FaqSettings />}
        {activeSection === 'contact' && <ContactSettings />}
        {activeSection === 'footer' && <FooterSettings />}
        {activeSection === 'blogs' && <BlogsSettings />}
      </main>
    </div>
  );
};

export default AdminDashboard;

